import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { formatRange, formatMoney } from "../lib/format";
import { useConfirm, useAlert } from "../components/Confirm";

type Tab = "all" | "draft" | "submitted" | "approved" | "rejected";

/** Per-module routes for future tile clicks. As each module ships its own page, drop a route in here. */
const MODULE_ROUTES: Record<string, string> = {
  // holiday family routes are hardcoded below (3 different tiles share this module)
  case:   "/case/new",
  uplift: "/uplift/new",
  awf:    "/awf/new",
};

export function Landing() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const alertModal = useAlert();
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  const list = useQuery({ queryKey: ["my-estimates"], queryFn: () => api.myEstimates() });
  const modules = useQuery({ queryKey: ["modules"], queryFn: api.modules });
  const [tab, setTab] = useState<Tab>("all");
  // Bulk selection state - which estimate ids are ticked. Cleared when tab/data changes.
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [bulkBusy, setBulkBusy] = useState(false);

  const all: any[] = list.data?.estimates ?? [];
  const counts = useMemo(() => ({
    all:       all.length,
    draft:     all.filter((x) => x.status === "draft").length,
    submitted: all.filter((x) => x.status === "submitted").length,
    approved:  all.filter((x) => x.status === "approved").length,
    rejected:  all.filter((x) => x.status === "rejected").length,
  }), [all]);
  const filtered = tab === "all" ? all : all.filter((x) => x.status === tab);

  const totalApproved = useMemo(() =>
    all.filter((x) => x.status === "approved").reduce((s, x) => s + Number(x.total_amount ?? 0), 0),
  [all]);

  // Clear selection whenever the user switches tabs or the dataset reloads.
  useEffect(() => { setSelected({}); }, [tab, list.dataUpdatedAt]);

  const selectedIds = Object.keys(selected).filter((id) => selected[id]);
  const selectedRows = filtered.filter((r) => selected[r.id]);
  const allDrafts     = selectedRows.length > 0 && selectedRows.every((r) => r.status === "draft");
  const anyDrafts     = selectedRows.some((r) => r.status === "draft");

  function toggleAllVisible() {
    if (selectedIds.length === filtered.length) setSelected({});
    else setSelected(Object.fromEntries(filtered.map((r) => [r.id, true])));
  }

  async function bulkSubmit() {
    const ok = await confirm({
      title: `Submit ${selectedIds.length} estimate${selectedIds.length === 1 ? "" : "s"} for approval?`,
      body: "Approvers will be notified for each. Drafts only.",
      confirmLabel: `Submit ${selectedIds.length}`,
    });
    if (!ok) return;
    setBulkBusy(true);
    try {
      const r = await api.estimatesBulk("submit", selectedIds);
      await qc.invalidateQueries({ queryKey: ["my-estimates"] });
      setSelected({});
      if (r.failures > 0) {
        await alertModal({
          title: `${r.successes} submitted, ${r.failures} skipped`,
          body: "Skipped items were either not drafts or no longer exist.",
          confirmLabel: "OK",
        });
      }
    } catch (err) {
      await alertModal({ title: "Bulk submit failed", body: (err as Error).message, confirmLabel: "OK" });
    } finally { setBulkBusy(false); }
  }

  async function bulkDelete() {
    const ok = await confirm({
      title: `Delete ${selectedIds.length} draft${selectedIds.length === 1 ? "" : "s"}?`,
      body: "This permanently removes the selected drafts. Only drafts can be deleted.",
      danger: true,
      confirmLabel: `Delete ${selectedIds.length}`,
    });
    if (!ok) return;
    setBulkBusy(true);
    try {
      const r = await api.estimatesBulk("delete", selectedIds);
      await qc.invalidateQueries({ queryKey: ["my-estimates"] });
      setSelected({});
      if (r.failures > 0) {
        await alertModal({
          title: `${r.successes} deleted, ${r.failures} skipped`,
          body: "Skipped items weren't drafts.",
          confirmLabel: "OK",
        });
      }
    } catch (err) {
      await alertModal({ title: "Bulk delete failed", body: (err as Error).message, confirmLabel: "OK" });
    } finally { setBulkBusy(false); }
  }

  // Per-user module access. publicModulesFn returns { modules, access } where access is
  // "all" (admin/approver), "scoped" (at least one module assigned), or "none".
  const accessibleModules: string[] = (modules.data?.modules ?? []).map((m: any) => m.key);
  const accessFlag: string | undefined = modules.data?.access;
  const hasHolidayAccess = accessibleModules.includes("holiday");

  // No modules at all? Show the "request access" screen instead of a working landing.
  if (modules.isSuccess && accessFlag === "none") {
    return (
      <div className="max-w-xl mx-auto mt-16 bg-white border border-slate-200 rounded-2xl p-8 text-center">
        <div className="text-5xl mb-3">🔒</div>
        <h1 className="text-2xl font-semibold text-liaise-navy">No access yet</h1>
        <p className="text-slate-600 mt-2">
          You're signed in, but no pricing modules have been assigned to your account.
          Please ask the IT or Commercial admin to grant you access.
        </p>
        <p className="text-slate-400 text-xs mt-6">{me.data?.email}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* greeting */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-liaise-navy">Pricing</h1>
          <p className="text-slate-500 mt-1">
            Welcome back{me.data?.name ? `, ${me.data.name.split(" ")[0]}` : ""}. Liaise commercial workspace.
          </p>
        </div>
        {hasHolidayAccess && (
          <Link to="/holiday/new" className="px-4 py-2 rounded-lg bg-liaise-navy text-white text-sm font-medium hover:opacity-90">
            + New estimate
          </Link>
        )}
      </div>

      {/* tile grid with live counts (filtered by tile type) */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Holiday family — three tiles, all backed by the holiday module.
            Only rendered if the user has access to the holiday module. */}
        {hasHolidayAccess && (
          <>
            <Tile to="/holiday/new?type=holiday"
                  icon="🏖" title="Holiday pricing"
                  desc="Calculate and submit a holiday cost to the funder."
                  live badge={badgeFor(all, "holiday")} />
            <Tile to="/holiday/new?type=hospital"
                  icon="🏥" title="Hospital-stay pricing"
                  desc="Same rates as holiday. Use when a PWS is admitted or discharged."
                  live badge={badgeFor(all, "hospital")} />
            <Tile to="/holiday/new?type=transition"
                  icon="🤝" title="Transition pricing"
                  desc="Transitional hours for a PWS arriving at a new home."
                  live badge={badgeFor(all, "transition")} />
          </>
        )}
        {/* Other modules — driven by the module registry so admin can flip them on */}
        {(modules.data?.modules ?? [])
          .filter((m: any) => m.key !== "holiday" && m.key !== "system")
          .map((m: any) => {
            const live = m.status === "enabled";
            const route = MODULE_ROUTES[m.key];
            return (
              <Tile
                key={m.key}
                to={live && route ? route : undefined}
                icon={m.icon || "•"}
                title={m.display_name}
                desc={m.description || ""}
                live={live}
                badge={live ? badgeFor(all, m.key) : (m.status === "planned" ? "Planned" : "Coming soon")}
              />
            );
          })}
      </div>

      {/* personal stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total" value={counts.all.toString()} />
        <StatCard label="In review" value={counts.submitted.toString()} highlight={counts.submitted > 0} />
        <StatCard label="Approved" value={counts.approved.toString()} />
        <StatCard label="Approved £" value={formatMoney(totalApproved)} />
      </div>

      {/* estimates list */}
      <section>
        <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
          <h2 className="text-xl font-semibold text-liaise-navy">My estimates</h2>
          <div className="flex flex-wrap gap-1 text-xs">
            <FilterTab tab="all"       label={`All (${counts.all})`}                  active={tab} setTab={setTab} />
            <FilterTab tab="draft"     label={`Drafts (${counts.draft})`}             active={tab} setTab={setTab} />
            <FilterTab tab="submitted" label={`In review (${counts.submitted})`}      active={tab} setTab={setTab} />
            <FilterTab tab="approved"  label={`Approved (${counts.approved})`}        active={tab} setTab={setTab} />
            <FilterTab tab="rejected"  label={`Rejected (${counts.rejected})`}        active={tab} setTab={setTab} />
          </div>
        </div>

        {/* Bulk action bar — only appears once a row is ticked. */}
        {selectedIds.length > 0 && (
          <div className="mb-3 bg-liaise-navy text-white rounded-xl p-3 flex flex-wrap items-center gap-3 text-sm">
            <div className="font-medium">{selectedIds.length} selected</div>
            <div className="flex-1" />
            {anyDrafts && (
              <button onClick={bulkSubmit} disabled={bulkBusy || !allDrafts}
                title={allDrafts ? "Submit all selected drafts for approval" : "Selection contains non-drafts. Pick only drafts to enable submit."}
                className="px-3 py-1.5 rounded-lg bg-liaise-green text-white text-sm font-medium disabled:opacity-50">
                {bulkBusy ? "Working..." : `Submit ${selectedRows.filter((r) => r.status === "draft").length}`}
              </button>
            )}
            {anyDrafts && (
              <button onClick={bulkDelete} disabled={bulkBusy || !allDrafts}
                title={allDrafts ? "Delete all selected drafts" : "Only drafts can be deleted."}
                className="px-3 py-1.5 rounded-lg bg-liaise-red text-white text-sm font-medium disabled:opacity-50">
                Delete drafts
              </button>
            )}
            <button onClick={() => setSelected({})} className="text-xs text-white/70 hover:text-white underline">Clear</button>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          {list.isLoading && <div className="p-6 text-slate-500 text-sm">Loading...</div>}
          {list.error && <div className="p-6 text-red-600 text-sm">Failed to load. {(list.error as Error).message}</div>}

          {list.data && filtered.length === 0 && (
            <div className="p-10 text-center text-slate-500">
              <div className="text-3xl mb-2">📋</div>
              <div className="text-sm">{tab === "all" ? "No estimates yet" : `No ${tab} estimates`}</div>
              <div className="mt-3 flex flex-wrap justify-center gap-x-4 gap-y-1 text-sm">
                <Link to="/holiday/new?type=holiday"    className="text-liaise-blue hover:underline">🏖 New holiday</Link>
                <Link to="/holiday/new?type=hospital"   className="text-liaise-blue hover:underline">🏥 New hospital-stay</Link>
                <Link to="/holiday/new?type=transition" className="text-liaise-blue hover:underline">🤝 New transition</Link>
              </div>
            </div>
          )}

          {list.data && filtered.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="px-4 py-2 w-8">
                    <input type="checkbox"
                      checked={selectedIds.length > 0 && selectedIds.length === filtered.length}
                      onChange={toggleAllVisible}
                      aria-label="Select all" />
                  </th>
                  <th className="text-left px-4 py-2">Ref</th>
                  <th className="text-left px-4 py-2">Type</th>
                  <th className="text-left px-4 py-2">PWS</th>
                  <th className="text-left px-4 py-2">Home</th>
                  <th className="text-left px-4 py-2">Funder</th>
                  <th className="text-left px-4 py-2">Dates</th>
                  <th className="text-right px-4 py-2">Total</th>
                  <th className="text-left px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e: any) => (
                  <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <input type="checkbox" checked={!!selected[e.id]}
                        onChange={(ev) => setSelected((s) => ({ ...s, [e.id]: ev.target.checked }))}
                        aria-label={`Select ${e.request_number}`} />
                    </td>
                    <td className="px-4 py-2">
                      <Link to={`/${e.type === "case" ? "case" : "holiday"}/${e.id}`} className="text-liaise-blue hover:underline">
                        {e.request_number}
                      </Link>
                    </td>
                    <td className="px-4 py-2"><TypeBadge type={e.type ?? "holiday"} /></td>
                    <td className="px-4 py-2">{e.pws_initials}</td>
                    <td className="px-4 py-2 text-slate-700">{e.home_name ?? "-"}</td>
                    <td className="px-4 py-2 text-slate-700">{e.funder_name ?? "-"}</td>
                    <td className="px-4 py-2 text-slate-500 whitespace-nowrap">{formatRange(e.start_date, e.end_date)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{formatMoney(e.total_amount)}</td>
                    <td className="px-4 py-2"><StatusPill status={e.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {me.data?.roles?.approver && (
        <div className="text-sm text-slate-500 border-t pt-4 border-slate-200">
          You are an approver. Open the admin site at <a className="text-liaise-blue underline" href="https://pricing-admin.liaisegroup.com">pricing-admin.liaisegroup.com</a> to review the queue.
        </div>
      )}
    </div>
  );
}

function Tile({ to, icon, title, desc, live, badge }: { to?: string; icon: string; title: string; desc: string; live?: boolean; badge?: string }) {
  const inner = (
    <div className={`p-6 rounded-2xl border h-full transition ${live ? "bg-white border-slate-200 hover:border-liaise-navy hover:shadow-sm" : "bg-white border-slate-200 opacity-60"}`}>
      <div className="text-3xl mb-3">{icon}</div>
      <div className="text-lg font-semibold text-liaise-navy">{title}</div>
      <div className="text-sm text-slate-500 mt-1">{desc}</div>
      {!live && <div className="mt-3 text-xs text-slate-400">Coming soon</div>}
      {live && badge && (
        <div className="mt-3 text-xs inline-block px-2 py-1 rounded-full bg-liaise-yellow/30 text-amber-900">{badge}</div>
      )}
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

function StatCard({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`rounded-xl border px-4 py-3 ${highlight ? "border-liaise-orange bg-liaise-orange/5" : "border-slate-200 bg-white"}`}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-xl font-semibold text-liaise-navy tabular-nums">{value}</div>
    </div>
  );
}

function FilterTab({ tab, label, active, setTab }: { tab: Tab; label: string; active: Tab; setTab: (t: Tab) => void }) {
  const isActive = tab === active;
  return (
    <button
      onClick={() => setTab(tab)}
      className={`px-3 py-1 rounded-full ${isActive ? "bg-liaise-navy text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"}`}
    >
      {label}
    </button>
  );
}

function badgeFor(all: any[], type: string): string {
  const own = all.filter((x) => (x.type ?? "holiday") === type);
  const sub = own.filter((x) => x.status === "submitted").length;
  const dft = own.filter((x) => x.status === "draft").length;
  if (sub > 0) return `${sub} awaiting approval`;
  if (dft > 0) return `${dft} draft`;
  return "";
}

function TypeBadge({ type }: { type: string }) {
  const map: Record<string, { label: string; icon: string; classes: string }> = {
    holiday:     { label: "Holiday",      icon: "🏖", classes: "bg-liaise-blue/10 text-liaise-blue" },
    hospital:    { label: "Hospital",     icon: "🏥", classes: "bg-liaise-red/10 text-liaise-red" },
    transition:  { label: "Transition",   icon: "🤝", classes: "bg-liaise-green/15 text-emerald-800" },
    "case":      { label: "Case-by-case", icon: "📋", classes: "bg-liaise-purple/15 text-liaise-purple" },
    uplift:      { label: "Uplift",       icon: "📈", classes: "bg-liaise-yellow/30 text-amber-900" },
    awf:         { label: "AWF",          icon: "🎯", classes: "bg-liaise-orange/15 text-amber-900" },
  };
  const m = map[type] ?? { label: type, icon: "•", classes: "bg-slate-100 text-slate-700" };
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${m.classes}`}>
      <span>{m.icon}</span><span>{m.label}</span>
    </span>
  );
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    draft:     "bg-slate-100 text-slate-700",
    submitted: "bg-liaise-yellow/30 text-amber-900",
    approved:  "bg-liaise-green/20 text-emerald-900",
    rejected:  "bg-liaise-red/15 text-red-700",
    recalled:  "bg-slate-100 text-slate-500",
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full ${map[status] ?? "bg-slate-100"}`}>
      {status}
    </span>
  );
}
